import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  FileText, MapPin, Clock, BookOpen, Heart, ArrowLeft, Download, Phone, Mail,
  Bus, Car, Users, AlertTriangle, Utensils, Apple, CreditCard, AlertCircle,
  GraduationCap, BookMarked, Database, Calendar, Stethoscope, Shield,
  ClipboardList, Shirt, Laptop, UserCheck, HandHeart
} from "lucide-react";

// ==================== FORMS & DOCUMENTS ====================

export const MedicalReleaseForm = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8" />
              <CardTitle className="text-2xl">Medical Release Form</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <p className="text-red-800 font-semibold">Important Notice</p>
              <p className="text-red-700 text-sm">This form must be completed annually and updated whenever there are changes to your child's medical information.</p>
            </div>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Purpose</h3>
              <p className="text-gray-600">The Medical Release Form authorizes Young Christian Preparatory School to provide emergency medical treatment to your child in case of an accident, injury, or sudden illness when parents/guardians cannot be reached.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Required Information</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li><strong>Student Information:</strong> Full name, date of birth, grade level, student ID</li>
                <li><strong>Primary Physician:</strong> Name, clinic address, phone number</li>
                <li><strong>Insurance Information:</strong> Provider name, policy number, group number</li>
                <li><strong>Medical Conditions:</strong> Allergies, chronic illnesses, disabilities</li>
                <li><strong>Current Medications:</strong> Name, dosage, frequency, purpose</li>
                <li><strong>Emergency Contacts:</strong> Minimum of 3 contacts with phone numbers</li>
                <li><strong>Preferred Hospital:</strong> Name and location of preferred medical facility</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Authorization Includes</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Emergency Treatment</h4>
                  <p className="text-sm text-gray-600">Authorization for emergency medical care, including transportation to hospital</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Medication Administration</h4>
                  <p className="text-sm text-gray-600">Permission for school nurse to administer prescribed medications</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">First Aid Treatment</h4>
                  <p className="text-sm text-gray-600">Basic first aid for minor injuries and illnesses</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Medical Records Release</h4>
                  <p className="text-sm text-gray-600">Sharing of relevant medical information with healthcare providers</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Submission Instructions</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Download and complete the Medical Release Form</li>
                <li>Have the form signed by parent/legal guardian</li>
                <li>Attach a copy of your insurance card (front and back)</li>
                <li>Submit to the school nurse's office before the first day of school</li>
                <li>Update immediately if any medical information changes</li>
              </ol>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Form (PDF)
              </Button>
              <Button variant="outline">
                <Phone className="mr-2" size={16} /> Contact Nurse Office: (+055-2184037)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const FieldTripPermission = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Bus className="w-8 h-8" />
              <CardTitle className="text-2xl">Field Trip Permission Form</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">About Field Trips</h3>
              <p className="text-gray-600">Field trips are an integral part of our educational program at YCPS. They provide students with hands-on learning experiences that complement classroom instruction and expose them to new environments, cultures, and ideas.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Permission Requirements</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li><strong>Written Consent:</strong> Signed permission slip required for each trip</li>
                <li><strong>Emergency Contact:</strong> Updated contact information on file</li>
                <li><strong>Medical Information:</strong> Any relevant health concerns or allergies</li>
                <li><strong>Deadline:</strong> Forms must be submitted at least 3 days before the trip</li>
                <li><strong>Payment:</strong> Trip fees (if applicable) must be paid with the form</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Upcoming Field Trips</h3>
              <div className="space-y-3">
                {[
                  { date: "March 15, 2026", destination: "Kakum National Park", grade: "Primary 4-6", fee: "GH₵ 50" },
                  { date: "April 5, 2026", destination: "Cape Coast Castle", grade: "JHS 1-3", fee: "GH₵ 75" },
                  { date: "April 20, 2026", destination: "Takoradi Harbor Tour", grade: "All Grades", fee: "GH₵ 30" }
                ].map((trip, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <div>
                      <p className="font-semibold text-[#002147]">{trip.destination}</p>
                      <p className="text-sm text-gray-600">{trip.date} • {trip.grade}</p>
                    </div>
                    <span className="bg-[#FF6B00] text-white px-3 py-1 rounded-full text-sm">{trip.fee}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Parent/Guardian Responsibilities</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Ensure your child arrives at school on time on the day of the trip</li>
                <li>Provide appropriate clothing and supplies as specified</li>
                <li>Pack a healthy lunch and snacks unless otherwise arranged</li>
                <li>Inform teachers of any last-minute health concerns</li>
                <li>Be available by phone during the trip hours</li>
              </ul>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Permission Form
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const TranscriptRequest = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8" />
              <CardTitle className="text-2xl">Transcript Request</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Official Transcripts</h3>
              <p className="text-gray-600">Official transcripts are certified academic records bearing the school seal and registrar's signature. They are required for school transfers, scholarship applications, and college admissions.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Types of Transcripts</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <h4 className="font-semibold text-[#002147]">Official Transcript</h4>
                  <p className="text-sm text-gray-600">Sealed document sent directly to institutions</p>
                  <p className="text-[#FF6B00] font-semibold mt-2">GH₵ 50 per copy</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-green-500">
                  <h4 className="font-semibold text-[#002147]">Unofficial Transcript</h4>
                  <p className="text-sm text-gray-600">Student copy for personal records</p>
                  <p className="text-[#FF6B00] font-semibold mt-2">GH₵ 20 per copy</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Request Process</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Complete the Transcript Request Form with all required information</li>
                <li>Pay the applicable fee at the school bursar's office</li>
                <li>Submit the form and payment receipt to the registrar's office</li>
                <li>Allow 5-7 working days for processing</li>
                <li>Transcripts will be mailed directly to the institution or available for pickup</li>
              </ol>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Required Information</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Student's full name (as it appears on school records)</li>
                <li>Date of birth and student ID number</li>
                <li>Years of attendance at YCPS</li>
                <li>Receiving institution's name and complete address</li>
                <li>Purpose of transcript request</li>
                <li>Number of copies required</li>
              </ul>
            </section>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <p className="text-yellow-800 font-semibold">Processing Time</p>
              <p className="text-yellow-700 text-sm">Standard processing takes 5-7 working days. Rush service (2-3 days) available for an additional GH₵ 30.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Request Form
              </Button>
              <Button variant="outline">
                <Mail className="mr-2" size={16} /> Email: registrar@ycpschool.com
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const ScheduleChangeForm = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8" />
              <CardTitle className="text-2xl">Schedule Change Form</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Schedule Change Policy</h3>
              <p className="text-gray-600">Schedule changes are permitted during the first two weeks of each term. Requests after this period require special approval from the academic coordinator and are granted only under exceptional circumstances.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Valid Reasons for Schedule Change</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Course prerequisite not met</li>
                <li>Schedule conflict with required courses</li>
                <li>Medical or health-related issues (with documentation)</li>
                <li>Academic level adjustment (with teacher recommendation)</li>
                <li>Extracurricular activity conflicts (sports, clubs)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Request Process</h3>
              <div className="space-y-3">
                {[
                  { step: 1, title: "Consult with Teacher", desc: "Discuss the change with your current teacher and proposed teacher" },
                  { step: 2, title: "Complete Form", desc: "Fill out the Schedule Change Form with all required signatures" },
                  { step: 3, title: "Parent Approval", desc: "Obtain parent/guardian signature on the form" },
                  { step: 4, title: "Submit to Office", desc: "Submit to the academic coordinator's office" },
                  { step: 5, title: "Await Decision", desc: "Decision will be communicated within 3 working days" }
                ].map((item) => (
                  <div key={item.step} className="flex gap-4 items-start">
                    <div className="w-8 h-8 bg-[#FF6B00] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      {item.step}
                    </div>
                    <div>
                      <p className="font-semibold text-[#002147]">{item.title}</p>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <p className="text-blue-800 font-semibold">Important Deadline</p>
              <p className="text-blue-700 text-sm">Schedule change requests must be submitted within the first 10 school days of each term.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Form
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== TRANSPORTATION ====================

export const BusRoutes = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Bus className="w-8 h-8" />
              <CardTitle className="text-2xl">Bus Routes & Schedules</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">School Transportation Service</h3>
              <p className="text-gray-600">YCPS operates a fleet of 8 school buses serving major areas of Takoradi and surrounding communities. Our buses are equipped with GPS tracking, safety features, and are driven by licensed, trained drivers.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Bus Routes</h3>
              <div className="space-y-4">
                {[
                  { route: "Route 1 - Airport Area", stops: ["Airport Junction", "Chapel Hill", "Beach Road", "Main Campus"], time: "6:30 AM", fee: "GH₵ 200/term" },
                  { route: "Route 2 - Market Circle", stops: ["Market Circle", "Kokompe", "European Town", "Main Campus"], time: "6:45 AM", fee: "GH₵ 180/term" },
                  { route: "Route 3 - Effia", stops: ["Effia-Kwesimintsim", "New Site", "Tanokrom", "Main Campus"], time: "6:30 AM", fee: "GH₵ 220/term" },
                  { route: "Route 4 - Anaji", stops: ["Anaji", "Fijai", "Windy Ridge", "Main Campus"], time: "6:40 AM", fee: "GH₵ 200/term" }
                ].map((route, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-[#002147]">{route.route}</h4>
                      <span className="bg-[#FF6B00] text-white px-3 py-1 rounded-full text-sm">{route.fee}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2"><strong>Departure:</strong> {route.time}</p>
                    <p className="text-sm text-gray-600"><strong>Stops:</strong> {route.stops.join(" → ")}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Bus Schedule</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800">Morning Pickup</h4>
                  <p className="text-blue-700">6:30 AM - 7:15 AM</p>
                  <p className="text-sm text-blue-600">Arrives at school by 7:30 AM</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800">Afternoon Drop-off</h4>
                  <p className="text-green-700">3:30 PM - 4:30 PM</p>
                  <p className="text-sm text-green-600">Departs school at 3:15 PM</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Bus Rules</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Students must be at the bus stop 5 minutes before scheduled pickup</li>
                <li>Remain seated while the bus is in motion</li>
                <li>No eating or drinking on the bus</li>
                <li>Keep noise levels appropriate</li>
                <li>Follow driver's instructions at all times</li>
              </ul>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Route Map
              </Button>
              <Button variant="outline">
                <Phone className="mr-2" size={16} /> Transport Office: 0552184040
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const ParkingInfo = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Car className="w-8 h-8" />
              <CardTitle className="text-2xl">Parking Information</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Campus Parking</h3>
              <p className="text-gray-600">YCPS provides designated parking areas for staff, parents, and visitors. All vehicles must display valid parking permits and follow campus traffic regulations.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Parking Zones</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <h4 className="font-semibold text-blue-800">Staff Parking (Zone A)</h4>
                  <p className="text-sm text-blue-700">Reserved for teachers and staff only</p>
                  <p className="text-sm text-blue-600">Permit required: Staff ID</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <h4 className="font-semibold text-green-800">Visitor Parking (Zone B)</h4>
                  <p className="text-sm text-green-700">For parents and visitors</p>
                  <p className="text-sm text-green-600">Max 2 hours during school hours</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                  <h4 className="font-semibold text-orange-800">Drop-off Zone (Zone C)</h4>
                  <p className="text-sm text-orange-700">Quick drop-off and pick-up only</p>
                  <p className="text-sm text-orange-600">No parking - 5 minute limit</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <h4 className="font-semibold text-purple-800">Event Parking (Zone D)</h4>
                  <p className="text-sm text-purple-700">Available during school events</p>
                  <p className="text-sm text-purple-600">Overflow parking area</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Parking Rules</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Speed limit on campus: 10 km/h</li>
                <li>No honking in school zones</li>
                <li>Yield to pedestrians at all crossings</li>
                <li>Handicapped spaces reserved for permit holders only</li>
                <li>No parking in fire lanes or loading zones</li>
                <li>Lock vehicles and secure valuables</li>
              </ul>
            </section>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <p className="text-yellow-800 font-semibold">Drop-off Times</p>
              <p className="text-yellow-700 text-sm">Morning: 6:45 AM - 7:30 AM | Afternoon: 3:00 PM - 3:45 PM</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const CarpoolProgram = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8" />
              <CardTitle className="text-2xl">Carpool Program</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">About the Program</h3>
              <p className="text-gray-600">Our carpool program connects families living in the same area to share transportation responsibilities. This reduces traffic congestion, saves fuel costs, and promotes community connections among YCPS families.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Benefits</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">💰</span>
                  </div>
                  <h4 className="font-semibold">Save Money</h4>
                  <p className="text-sm text-gray-600">Share fuel costs with other families</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">⏰</span>
                  </div>
                  <h4 className="font-semibold">Save Time</h4>
                  <p className="text-sm text-gray-600">Alternate driving days with others</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">🌍</span>
                  </div>
                  <h4 className="font-semibold">Help Environment</h4>
                  <p className="text-sm text-gray-600">Reduce carbon footprint</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">How to Join</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Register your interest through the Parent Portal or school office</li>
                <li>Provide your home address and preferred schedule</li>
                <li>We'll match you with families in your area</li>
                <li>Meet with potential carpool partners to arrange details</li>
                <li>Sign the Carpool Agreement form</li>
                <li>Receive your carpool parking permit</li>
              </ol>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Carpool Guidelines</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>All drivers must have valid driver's license and insurance</li>
                <li>Children must use appropriate car seats/seat belts</li>
                <li>Establish clear communication channels with carpool partners</li>
                <li>Be punctual - respect everyone's time</li>
                <li>Notify partners immediately of any schedule changes</li>
              </ul>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Registration Form
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const TrafficGuidelines = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8" />
              <CardTitle className="text-2xl">Traffic Guidelines</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <p className="text-red-800 font-semibold">Safety First</p>
              <p className="text-red-700 text-sm">The safety of our students is our top priority. Please follow all traffic guidelines carefully.</p>
            </div>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Campus Traffic Flow</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Enter campus through the main gate only</li>
                <li>Follow the one-way traffic flow (counterclockwise)</li>
                <li>Maximum speed: 10 km/h throughout campus</li>
                <li>Exit through the designated exit gate</li>
                <li>No U-turns on campus roads</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Drop-off Procedures</h3>
              <div className="space-y-3">
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-[#FF6B00] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <p className="text-gray-600">Pull up to the designated drop-off zone in single file</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-[#FF6B00] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <p className="text-gray-600">Have children ready to exit from the right side of the vehicle</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-[#FF6B00] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <p className="text-gray-600">Children should exit quickly and safely</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-[#FF6B00] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <p className="text-gray-600">Drive forward immediately after drop-off</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Pick-up Procedures</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Display your pick-up tag clearly on the dashboard</li>
                <li>Wait in the designated pick-up queue</li>
                <li>Do not leave your vehicle in the pick-up lane</li>
                <li>Teachers will escort children to vehicles</li>
                <li>If your child is not ready, proceed to the waiting area</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Prohibited Actions</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {["Double parking", "Blocking crosswalks", "Using mobile phones while driving", "Honking unnecessarily", "Passing other vehicles in queue", "Parking in fire lanes"].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-red-600">
                    <span>❌</span> {item}
                  </div>
                ))}
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== DINING SERVICES ====================

export const LunchMenu = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Utensils className="w-8 h-8" />
              <CardTitle className="text-2xl">Lunch Menu</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Weekly Menu</h3>
              <p className="text-gray-600 mb-4">Our school cafeteria serves nutritious, locally-sourced meals prepared fresh daily. All meals include a main dish, side, fruit, and drink.</p>
              
              <div className="space-y-3">
                {[
                  { day: "Monday", main: "Jollof Rice with Chicken", side: "Coleslaw, Plantain", price: "GH₵ 15" },
                  { day: "Tuesday", main: "Banku with Okro Soup", side: "Fish, Pepper", price: "GH₵ 12" },
                  { day: "Wednesday", main: "Fried Rice with Vegetables", side: "Grilled Chicken", price: "GH₵ 15" },
                  { day: "Thursday", main: "Fufu with Light Soup", side: "Goat Meat, Garden Eggs", price: "GH₵ 14" },
                  { day: "Friday", main: "Waakye Special", side: "Spaghetti, Fish, Egg, Gari", price: "GH₵ 16" }
                ].map((menu, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <div>
                      <p className="font-semibold text-[#002147]">{menu.day}</p>
                      <p className="text-gray-700">{menu.main}</p>
                      <p className="text-sm text-gray-500">{menu.side}</p>
                    </div>
                    <span className="bg-[#FF6B00] text-white px-3 py-1 rounded-full text-sm">{menu.price}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Snack Options</h3>
              <div className="grid md:grid-cols-3 gap-3">
                {[
                  { item: "Meat Pie", price: "GH₵ 5" },
                  { item: "Spring Rolls", price: "GH₵ 4" },
                  { item: "Fresh Fruit Cup", price: "GH₵ 3" },
                  { item: "Yogurt", price: "GH₵ 4" },
                  { item: "Bofrot (Doughnut)", price: "GH₵ 2" },
                  { item: "Fruit Juice", price: "GH₵ 3" }
                ].map((snack, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                    <span>{snack.item}</span>
                    <span className="font-semibold text-[#FF6B00]">{snack.price}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Lunch Schedule</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800">Preschool & Primary 1-3</h4>
                  <p className="text-blue-700">11:30 AM - 12:15 PM</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800">Primary 4-6 & JHS</h4>
                  <p className="text-green-700">12:30 PM - 1:15 PM</p>
                </div>
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Full Menu (PDF)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const NutritionInfo = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Apple className="w-8 h-8" />
              <CardTitle className="text-2xl">Nutrition Information</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Our Nutrition Philosophy</h3>
              <p className="text-gray-600">At YCPS, we believe that good nutrition is essential for academic success. Our meals are designed to provide balanced nutrition that fuels learning and promotes healthy development.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Nutritional Standards</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800">✓ What We Include</h4>
                  <ul className="text-sm text-green-700 mt-2 space-y-1">
                    <li>• Fresh vegetables daily</li>
                    <li>• Lean proteins</li>
                    <li>• Whole grains</li>
                    <li>• Fresh fruits</li>
                    <li>• Low-fat dairy options</li>
                  </ul>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-800">✗ What We Limit</h4>
                  <ul className="text-sm text-red-700 mt-2 space-y-1">
                    <li>• Added sugars</li>
                    <li>• Excessive salt</li>
                    <li>• Trans fats</li>
                    <li>• Artificial preservatives</li>
                    <li>• Processed foods</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Daily Nutritional Goals</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Nutrient</th>
                      <th className="text-right py-2">Per Lunch</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b"><td className="py-2">Calories</td><td className="text-right">550-650 kcal</td></tr>
                    <tr className="border-b"><td className="py-2">Protein</td><td className="text-right">15-25g</td></tr>
                    <tr className="border-b"><td className="py-2">Carbohydrates</td><td className="text-right">60-80g</td></tr>
                    <tr className="border-b"><td className="py-2">Fiber</td><td className="text-right">5-8g</td></tr>
                    <tr><td className="py-2">Sodium</td><td className="text-right">&lt;640mg</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Tips for Parents</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Send a water bottle - we encourage hydration throughout the day</li>
                <li>Pack healthy snacks if your child needs extra nutrition</li>
                <li>Discuss food choices with your child</li>
                <li>Inform us of any dietary restrictions or allergies</li>
              </ul>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const MealPlans = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8" />
              <CardTitle className="text-2xl">Meal Plan Options</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Available Plans</h3>
              <div className="space-y-4">
                {[
                  { name: "Basic Plan", meals: "Lunch only (5 days/week)", price: "GH₵ 250/month", features: ["Hot lunch daily", "Fruit included", "Drink included"] },
                  { name: "Standard Plan", meals: "Lunch + Morning Snack", price: "GH₵ 350/month", features: ["Everything in Basic", "Morning snack at break", "Priority seating"] },
                  { name: "Premium Plan", meals: "All meals + Snacks", price: "GH₵ 450/month", features: ["Everything in Standard", "Afternoon snack", "Special event meals", "Birthday treat"] }
                ].map((plan, index) => (
                  <div key={index} className={`p-6 rounded-lg border-2 ${index === 2 ? 'border-[#FF6B00] bg-orange-50' : 'border-gray-200 bg-gray-50'}`}>
                    {index === 2 && <span className="bg-[#FF6B00] text-white text-xs px-2 py-1 rounded-full">Most Popular</span>}
                    <h4 className="font-bold text-xl text-[#002147] mt-2">{plan.name}</h4>
                    <p className="text-gray-600">{plan.meals}</p>
                    <p className="text-2xl font-bold text-[#FF6B00] my-3">{plan.price}</p>
                    <ul className="space-y-1">
                      {plan.features.map((f, i) => (
                        <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                          <span className="text-green-500">✓</span> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Term Discounts</h3>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-green-800">Pay for the full term and save 10%!</p>
                <ul className="text-sm text-green-700 mt-2">
                  <li>• Basic Plan: GH₵ 675/term (Save GH₵ 75)</li>
                  <li>• Standard Plan: GH₵ 945/term (Save GH₵ 105)</li>
                  <li>• Premium Plan: GH₵ 1,215/term (Save GH₵ 135)</li>
                </ul>
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]" onClick={() => navigate('/fees')}>
                Subscribe to Meal Plan
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const AllergyGuidelines = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8" />
              <CardTitle className="text-2xl">Allergy Guidelines</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <p className="text-red-800 font-semibold">Allergy Alert</p>
              <p className="text-red-700 text-sm">If your child has any food allergies, please inform the school immediately and complete an Allergy Action Plan.</p>
            </div>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Common Allergens in Our Kitchen</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {["Peanuts", "Tree Nuts", "Milk/Dairy", "Eggs", "Fish", "Shellfish", "Wheat/Gluten", "Soy"].map((allergen, i) => (
                  <div key={i} className="bg-yellow-50 p-3 rounded-lg text-center">
                    <span className="text-yellow-800 font-medium">{allergen}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Our Allergy Management Protocol</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>All students with allergies are registered in our system</li>
                <li>Kitchen staff are trained in allergy awareness</li>
                <li>Allergen-free alternatives are available upon request</li>
                <li>All meals are labeled with allergen information</li>
                <li>Epinephrine auto-injectors available in nurse's office</li>
                <li>Staff trained in emergency allergy response</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Parent Responsibilities</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Complete the Food Allergy Action Plan form</li>
                <li>Provide current allergy documentation from a physician</li>
                <li>Supply any required medications (EpiPen, antihistamines)</li>
                <li>Update the school of any changes in allergies</li>
                <li>Teach your child about their allergies and safe foods</li>
              </ol>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Emergency Contacts</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-semibold">School Nurse</p>
                  <p className="text-[#FF6B00]">(+055-2184037)</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-semibold">Emergency Services</p>
                  <p className="text-[#FF6B00]">112 / 191</p>
                </div>
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Allergy Action Plan
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== ACADEMIC RESOURCES ====================

export const TutoringServices = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-8 h-8" />
              <CardTitle className="text-2xl">Tutoring Services</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Academic Support Program</h3>
              <p className="text-gray-600">Our tutoring services provide personalized academic support to help students excel in their studies. Whether your child needs help catching up or wants to get ahead, our qualified tutors are here to help.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Subjects Offered</h3>
              <div className="grid md:grid-cols-3 gap-3">
                {["Mathematics", "English Language", "Science", "Social Studies", "French", "ICT", "Creative Arts", "Religious & Moral Ed.", "Ghanaian Language"].map((subject, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded-lg text-center">
                    <span className="text-gray-700">{subject}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Tutoring Options</h3>
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <h4 className="font-semibold text-blue-800">After-School Tutoring</h4>
                  <p className="text-sm text-blue-700">Monday - Thursday, 3:30 PM - 5:00 PM</p>
                  <p className="text-blue-600 font-semibold mt-2">GH₵ 100/month (per subject)</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <h4 className="font-semibold text-green-800">Weekend Classes</h4>
                  <p className="text-sm text-green-700">Saturday, 9:00 AM - 12:00 PM</p>
                  <p className="text-green-600 font-semibold mt-2">GH₵ 150/month (per subject)</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <h4 className="font-semibold text-purple-800">One-on-One Sessions</h4>
                  <p className="text-sm text-purple-700">By appointment</p>
                  <p className="text-purple-600 font-semibold mt-2">GH₵ 50/hour</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">How to Enroll</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Complete the Tutoring Request Form</li>
                <li>Meet with academic coordinator to assess needs</li>
                <li>Select subjects and schedule</li>
                <li>Pay fees at the bursar's office</li>
                <li>Begin tutoring sessions</li>
              </ol>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Request Form
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const StudyGuides = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <BookMarked className="w-8 h-8" />
              <CardTitle className="text-2xl">Study Guides</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Available Study Materials</h3>
              <p className="text-gray-600 mb-4">Download study guides and revision materials to help your child prepare for exams and reinforce classroom learning.</p>
              
              <div className="space-y-3">
                {[
                  { grade: "Primary 1-3", subjects: ["Mathematics Workbook", "English Reading Guide", "Basic Science Activities"] },
                  { grade: "Primary 4-6", subjects: ["BECE Prep - Mathematics", "BECE Prep - English", "BECE Prep - Science", "Social Studies Guide"] },
                  { grade: "JHS 1-3", subjects: ["BECE Past Questions", "Core Mathematics Guide", "Integrated Science Notes", "English Comprehension Practice"] }
                ].map((level, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-[#002147] mb-2">{level.grade}</h4>
                    <div className="flex flex-wrap gap-2">
                      {level.subjects.map((subject, i) => (
                        <Button key={i} variant="outline" size="sm" className="text-xs">
                          <Download size={12} className="mr-1" /> {subject}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Study Tips</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Create a consistent study schedule</li>
                <li>Find a quiet, well-lit study space</li>
                <li>Take short breaks every 30-45 minutes</li>
                <li>Use active recall techniques</li>
                <li>Practice with past exam questions</li>
                <li>Get enough sleep before exams</li>
              </ul>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const ResearchDatabases = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Database className="w-8 h-8" />
              <CardTitle className="text-2xl">Research Databases</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Online Learning Resources</h3>
              <p className="text-gray-600">Access educational databases and online resources to support research projects and extended learning.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Available Resources</h3>
              <div className="space-y-4">
                {[
                  { name: "Ghana Library Digital Archive", desc: "Access to Ghanaian historical documents and literature", access: "Free for students" },
                  { name: "Encyclopedia Britannica School", desc: "Comprehensive encyclopedia for all grade levels", access: "School subscription" },
                  { name: "National Geographic Kids", desc: "Science, nature, and geography resources", access: "Free access" },
                  { name: "Khan Academy", desc: "Free video lessons and practice exercises", access: "Free access" },
                  { name: "BBC Bitesize", desc: "Curriculum-aligned study materials", access: "Free access" }
                ].map((resource, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <div>
                      <h4 className="font-semibold text-[#002147]">{resource.name}</h4>
                      <p className="text-sm text-gray-600">{resource.desc}</p>
                    </div>
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs">{resource.access}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">How to Access</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Use your student ID to log in to the school portal</li>
                <li>Click on "Library Resources" in your dashboard</li>
                <li>Select the database you want to access</li>
                <li>For help, contact the school librarian</li>
              </ul>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const AcademicCalendar = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8" />
              <CardTitle className="text-2xl">Academic Calendar 2025-2026</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">First Term</h3>
              <div className="space-y-2">
                {[
                  { date: "September 4, 2025", event: "First Term Begins", type: "start" },
                  { date: "October 1, 2025", event: "Founders' Day Holiday", type: "holiday" },
                  { date: "October 15-17, 2025", event: "Mid-Term Break", type: "break" },
                  { date: "November 28, 2025", event: "First Term Exams Begin", type: "exam" },
                  { date: "December 12, 2025", event: "First Term Ends", type: "end" }
                ].map((item, i) => (
                  <div key={i} className={`p-3 rounded-lg flex justify-between items-center ${
                    item.type === 'holiday' ? 'bg-green-50' : 
                    item.type === 'exam' ? 'bg-red-50' : 
                    item.type === 'break' ? 'bg-yellow-50' : 'bg-blue-50'
                  }`}>
                    <span className="font-medium">{item.event}</span>
                    <span className="text-sm text-gray-600">{item.date}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Second Term</h3>
              <div className="space-y-2">
                {[
                  { date: "January 6, 2026", event: "Second Term Begins", type: "start" },
                  { date: "March 6, 2026", event: "Independence Day Holiday", type: "holiday" },
                  { date: "February 18-20, 2026", event: "Mid-Term Break", type: "break" },
                  { date: "March 27, 2026", event: "Second Term Exams Begin", type: "exam" },
                  { date: "April 10, 2026", event: "Second Term Ends", type: "end" }
                ].map((item, i) => (
                  <div key={i} className={`p-3 rounded-lg flex justify-between items-center ${
                    item.type === 'holiday' ? 'bg-green-50' : 
                    item.type === 'exam' ? 'bg-red-50' : 
                    item.type === 'break' ? 'bg-yellow-50' : 'bg-blue-50'
                  }`}>
                    <span className="font-medium">{item.event}</span>
                    <span className="text-sm text-gray-600">{item.date}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Third Term</h3>
              <div className="space-y-2">
                {[
                  { date: "April 27, 2026", event: "Third Term Begins", type: "start" },
                  { date: "May 1, 2026", event: "May Day Holiday", type: "holiday" },
                  { date: "May 27-29, 2026", event: "Mid-Term Break", type: "break" },
                  { date: "June 26, 2026", event: "Third Term Exams Begin", type: "exam" },
                  { date: "July 10, 2026", event: "Speech & Prize Giving Day", type: "event" },
                  { date: "July 17, 2026", event: "Third Term Ends", type: "end" }
                ].map((item, i) => (
                  <div key={i} className={`p-3 rounded-lg flex justify-between items-center ${
                    item.type === 'holiday' ? 'bg-green-50' : 
                    item.type === 'exam' ? 'bg-red-50' : 
                    item.type === 'break' ? 'bg-yellow-50' :
                    item.type === 'event' ? 'bg-purple-50' : 'bg-blue-50'
                  }`}>
                    <span className="font-medium">{item.event}</span>
                    <span className="text-sm text-gray-600">{item.date}</span>
                  </div>
                ))}
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Full Calendar (PDF)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== HEALTH & SAFETY ====================

export const NurseOffice = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Stethoscope className="w-8 h-8" />
              <CardTitle className="text-2xl">Nurse Office Information</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">School Health Services</h3>
              <p className="text-gray-600">The YCPS Health Office is staffed by a qualified school nurse who provides first aid, health screenings, medication administration, and health education to our students. Our goal is to keep students healthy and ready to learn.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Office Hours & Location</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800">Hours of Operation</h4>
                  <p className="text-blue-700">Monday - Friday</p>
                  <p className="text-blue-700">7:00 AM - 4:00 PM</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800">Location</h4>
                  <p className="text-green-700">Administration Building</p>
                  <p className="text-green-700">Ground Floor, Room 105</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Services Provided</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: "First Aid Treatment", desc: "Minor injuries, cuts, bruises, headaches" },
                  { title: "Medication Administration", desc: "With proper authorization and documentation" },
                  { title: "Health Screenings", desc: "Vision, hearing, and growth monitoring" },
                  { title: "Illness Assessment", desc: "Evaluation and parent notification" },
                  { title: "Health Records", desc: "Maintenance of student health files" },
                  { title: "Health Education", desc: "Hygiene, nutrition, and wellness programs" }
                ].map((service, i) => (
                  <div key={i} className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-[#002147]">{service.title}</h4>
                    <p className="text-sm text-gray-600">{service.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">When Students Visit the Nurse</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Feeling ill during school hours</li>
                <li>Injury during school activities</li>
                <li>Scheduled medication time</li>
                <li>Health screening appointments</li>
                <li>Need for first aid supplies</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Parent Guidelines</h3>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                <p className="text-yellow-800 font-semibold">When to Keep Your Child Home</p>
                <ul className="text-yellow-700 text-sm mt-2 space-y-1">
                  <li>• Fever of 37.8°C (100°F) or higher</li>
                  <li>• Vomiting or diarrhea within 24 hours</li>
                  <li>• Contagious conditions (pink eye, ringworm, etc.)</li>
                  <li>• Severe cold symptoms affecting learning ability</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Contact Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg flex items-center gap-3">
                  <Phone className="text-[#FF6B00]" />
                  <div>
                    <p className="font-semibold">Nurse Office</p>
                    <p className="text-[#FF6B00]">(+055-2184037) ext. 105</p>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg flex items-center gap-3">
                  <Mail className="text-[#FF6B00]" />
                  <div>
                    <p className="font-semibold">Email</p>
                    <p className="text-[#FF6B00]">nurse@ycpschool.com</p>
                  </div>
                </div>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const ImmunizationRequirements = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8" />
              <CardTitle className="text-2xl">Immunization Requirements</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <p className="text-red-800 font-semibold">Important Requirement</p>
              <p className="text-red-700 text-sm">All students must have up-to-date immunizations before enrollment. Students without proper documentation may be excluded from school.</p>
            </div>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Required Vaccinations</h3>
              <p className="text-gray-600 mb-4">In accordance with Ghana Health Service guidelines, the following immunizations are required for school attendance:</p>
              
              <div className="space-y-3">
                {[
                  { vaccine: "BCG (Tuberculosis)", doses: "1 dose", age: "At birth" },
                  { vaccine: "OPV (Oral Polio)", doses: "4 doses", age: "Birth, 6, 10, 14 weeks" },
                  { vaccine: "Pentavalent (DPT-HepB-Hib)", doses: "3 doses", age: "6, 10, 14 weeks" },
                  { vaccine: "Pneumococcal (PCV)", doses: "3 doses", age: "6, 10, 14 weeks" },
                  { vaccine: "Rotavirus", doses: "2 doses", age: "6, 10 weeks" },
                  { vaccine: "Measles-Rubella", doses: "2 doses", age: "9 months, 18 months" },
                  { vaccine: "Yellow Fever", doses: "1 dose", age: "9 months" },
                  { vaccine: "Meningitis A", doses: "1 dose", age: "18 months" }
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 p-4 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <div>
                      <p className="font-semibold text-[#002147]">{item.vaccine}</p>
                      <p className="text-sm text-gray-600">{item.age}</p>
                    </div>
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">{item.doses}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Documentation Required</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li><strong>Child Health Record Card:</strong> Original Ghana Health Service immunization card</li>
                <li><strong>Medical Certificate:</strong> If exemptions are needed for medical reasons</li>
                <li><strong>Updated Records:</strong> For students transferring from other schools</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Where to Get Vaccinations</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800">Takoradi Government Hospital</h4>
                  <p className="text-sm text-blue-700">Child Welfare Clinic</p>
                  <p className="text-sm text-blue-600">Monday - Friday, 8 AM - 2 PM</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800">Effia-Nkwanta Regional Hospital</h4>
                  <p className="text-sm text-green-700">Pediatric Department</p>
                  <p className="text-sm text-green-600">Monday - Friday, 8 AM - 4 PM</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Submission Deadline</h3>
              <p className="text-gray-600">Immunization records must be submitted:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mt-2">
                <li>New students: Before the first day of school</li>
                <li>Returning students: Within 30 days of any new vaccination</li>
                <li>Incoming students: With enrollment application</li>
              </ul>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Immunization Form
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const EmergencyProcedures = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8" />
              <CardTitle className="text-2xl">Emergency Procedures</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <p className="text-red-800 font-semibold">Emergency Numbers</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <p className="text-red-700 text-sm"><strong>Police:</strong> 191</p>
                <p className="text-red-700 text-sm"><strong>Fire:</strong> 192</p>
                <p className="text-red-700 text-sm"><strong>Ambulance:</strong> 193</p>
                <p className="text-red-700 text-sm"><strong>School Security:</strong> 0552184099</p>
              </div>
            </div>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Fire Emergency</h3>
              <div className="bg-orange-50 p-4 rounded-lg">
                <ol className="list-decimal list-inside space-y-2 text-orange-800">
                  <li>When the fire alarm sounds, stop all activities immediately</li>
                  <li>Leave all belongings - do not collect bags or personal items</li>
                  <li>Walk quickly and calmly to the nearest exit</li>
                  <li>Do not use elevators - use stairs only</li>
                  <li>Proceed to your class assembly point</li>
                  <li>Teachers will take attendance</li>
                  <li>Remain at assembly point until given "all clear"</li>
                </ol>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Lockdown Procedure</h3>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-purple-800 font-semibold mb-2">When "Lockdown" is announced:</p>
                <ol className="list-decimal list-inside space-y-2 text-purple-700">
                  <li>Immediately go to the nearest classroom</li>
                  <li>Teacher locks the door and covers windows</li>
                  <li>Move away from doors and windows</li>
                  <li>Sit quietly on the floor against interior wall</li>
                  <li>Silence all mobile devices</li>
                  <li>Wait for "all clear" announcement from administration</li>
                </ol>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Medical Emergency</h3>
              <div className="bg-blue-50 p-4 rounded-lg">
                <ol className="list-decimal list-inside space-y-2 text-blue-800">
                  <li>Stay calm and assess the situation</li>
                  <li>Do not move an injured person unless in immediate danger</li>
                  <li>Send someone to call the school nurse (ext. 105)</li>
                  <li>If nurse unavailable, call the main office</li>
                  <li>Stay with the injured person until help arrives</li>
                  <li>Provide first aid only if trained</li>
                </ol>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Severe Weather</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-2">In case of severe storms or flooding:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li>Move to interior rooms away from windows</li>
                  <li>Stay away from electrical equipment</li>
                  <li>Follow teacher instructions for shelter</li>
                  <li>Parents will be notified if early dismissal is necessary</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Assembly Points</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Main Campus</h4>
                  <p className="text-sm text-gray-600">School Football Field</p>
                  <p className="text-sm text-gray-500">Behind the main building</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Annex Campus</h4>
                  <p className="text-sm text-gray-600">Basketball Court Area</p>
                  <p className="text-sm text-gray-500">Near the main gate</p>
                </div>
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Download className="mr-2" size={16} /> Download Emergency Guide
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const MentalHealthResources = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <HandHeart className="w-8 h-8" />
              <CardTitle className="text-2xl">Mental Health Resources</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Our Commitment</h3>
              <p className="text-gray-600">At YCPS, we recognize that mental health is just as important as physical health. We are committed to providing a supportive environment where students feel safe to learn, grow, and seek help when needed.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">School Counseling Services</h3>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">School Counselor</h4>
                <p className="text-blue-700">[Teacher Name]</p>
                <p className="text-sm text-blue-600">Office: Administration Building, Room 202</p>
                <p className="text-sm text-blue-600">Hours: Monday - Friday, 8 AM - 3 PM</p>
                <p className="text-sm text-blue-600">Email: counselor@ycpschool.com</p>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Services Available</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: "Individual Counseling", desc: "One-on-one support for personal challenges" },
                  { title: "Group Sessions", desc: "Peer support groups for common issues" },
                  { title: "Crisis Intervention", desc: "Immediate support during emergencies" },
                  { title: "Academic Support", desc: "Help with study skills and stress" },
                  { title: "Family Consultation", desc: "Guidance for parents and families" },
                  { title: "Referral Services", desc: "Connection to external professionals" }
                ].map((service, i) => (
                  <div key={i} className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-[#002147]">{service.title}</h4>
                    <p className="text-sm text-gray-600">{service.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Signs to Watch For</h3>
              <p className="text-gray-600 mb-3">If you notice any of these changes in your child, consider seeking support:</p>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  "Persistent sadness or withdrawal",
                  "Changes in eating or sleeping patterns",
                  "Declining academic performance",
                  "Loss of interest in activities",
                  "Frequent physical complaints",
                  "Increased irritability or anger",
                  "Difficulty concentrating",
                  "Social isolation from friends"
                ].map((sign, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-600">
                    <span className="text-yellow-500">⚠️</span> {sign}
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">How to Get Help</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Talk to your class teacher or any trusted adult at school</li>
                <li>Visit the school counselor directly - no appointment needed</li>
                <li>Parents can request counseling services through the office</li>
                <li>In emergencies, contact the main office immediately</li>
              </ol>
            </section>

            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
              <p className="text-green-800 font-semibold">Remember</p>
              <p className="text-green-700 text-sm">Seeking help is a sign of strength, not weakness. All counseling services are confidential.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Phone className="mr-2" size={16} /> Contact Counselor
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== POLICIES ====================

export const StudentHandbook = () => {
  const navigate = useNavigate();
  const [downloading, setDownloading] = React.useState(false);
  
  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/resources/handbook/download`);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'YCPS_Student_Handbook_2025-2026.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download handbook. Please try again.');
    }
    setDownloading(false);
  };

  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8" />
              <CardTitle className="text-2xl">Student Handbook 2025-2026</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Welcome Message</h3>
              <p className="text-gray-600">Welcome to Young Christian Preparatory School! This handbook contains important information about our school's policies, procedures, and expectations. Please read it carefully and keep it for reference throughout the school year.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">School Mission</h3>
              <div className="bg-blue-50 p-4 rounded-lg italic text-blue-800">
                "To provide quality Christian education that nurtures the whole child - spiritually, academically, physically, and socially - preparing them to be responsible citizens and leaders of tomorrow."
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Handbook Contents</h3>
              <div className="space-y-2">
                {[
                  { chapter: "Chapter 1", title: "Academic Policies", page: "5" },
                  { chapter: "Chapter 2", title: "Attendance Requirements", page: "12" },
                  { chapter: "Chapter 3", title: "Code of Conduct", page: "18" },
                  { chapter: "Chapter 4", title: "Dress Code", page: "25" },
                  { chapter: "Chapter 5", title: "Health & Safety", page: "30" },
                  { chapter: "Chapter 6", title: "Technology Use", page: "38" },
                  { chapter: "Chapter 7", title: "Extracurricular Activities", page: "45" },
                  { chapter: "Chapter 8", title: "Parent Involvement", page: "52" },
                  { chapter: "Chapter 9", title: "Discipline Procedures", page: "58" },
                  { chapter: "Chapter 10", title: "Appendices & Forms", page: "65" }
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                    <div>
                      <span className="font-semibold text-[#002147]">{item.chapter}: </span>
                      <span className="text-gray-700">{item.title}</span>
                    </div>
                    <span className="text-gray-500 text-sm">Page {item.page}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">School Hours</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Preschool & Primary</h4>
                  <p className="text-gray-600">7:30 AM - 2:30 PM</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Junior High School</h4>
                  <p className="text-gray-600">7:30 AM - 3:00 PM</p>
                </div>
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                className="bg-[#FF6B00] hover:bg-[#E65C00]" 
                onClick={handleDownloadPDF}
                disabled={downloading}
                data-testid="download-handbook-btn"
              >
                {downloading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span> Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="mr-2" size={16} /> Download Full Handbook (PDF)
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const DressCode = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Shirt className="w-8 h-8" />
              <CardTitle className="text-2xl">Dress Code Policy</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Uniform Requirements</h3>
              <p className="text-gray-600">All students are required to wear the official YCPS uniform during school hours. Proper uniform promotes school identity, equality among students, and a focused learning environment.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Boys' Uniform</h3>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Preschool & Primary</h4>
                <ul className="list-disc list-inside text-blue-700 space-y-1">
                  <li>Yellow short-sleeve shirt with school crest</li>
                  <li>Brown shorts (Primary 1-3) or trousers (Primary 4-6)</li>
                  <li>Brown belt</li>
                  <li>Brown socks and black shoes</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg mt-3">
                <h4 className="font-semibold text-green-800 mb-2">Junior High School</h4>
                <ul className="list-disc list-inside text-green-700 space-y-1">
                  <li>Blue long-sleeve shirt with school crest</li>
                  <li>Navy blue trousers</li>
                  <li>Navy blue tie</li>
                  <li>Black belt, black socks, and black shoes</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Girls' Uniform</h3>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Preschool & Primary</h4>
                <ul className="list-disc list-inside text-blue-700 space-y-1">
                  <li>Yellow short-sleeve blouse with school crest</li>
                  <li>Brown pinafore dress (knee-length)</li>
                  <li>Brown socks and black shoes</li>
                  <li>Hair neatly styled with brown/black accessories only</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg mt-3">
                <h4 className="font-semibold text-green-800 mb-2">Junior High School</h4>
                <ul className="list-disc list-inside text-green-700 space-y-1">
                  <li>Blue long-sleeve blouse with school crest</li>
                  <li>Navy blue skirt (knee-length)</li>
                  <li>Navy blue tie</li>
                  <li>Black socks and black shoes</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">PE Uniform</h3>
              <div className="bg-orange-50 p-4 rounded-lg">
                <ul className="list-disc list-inside text-orange-700 space-y-1">
                  <li>School PE t-shirt (yellow with school logo)</li>
                  <li>Navy blue shorts</li>
                  <li>White sports socks</li>
                  <li>Athletic shoes (trainers/sneakers)</li>
                </ul>
                <p className="text-sm text-orange-600 mt-2">PE uniform is worn only on scheduled PE days.</p>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Not Permitted</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  "Jewelry (except small stud earrings for girls)",
                  "Makeup or nail polish",
                  "Colored hair or hair extensions",
                  "Sandals or open-toed shoes",
                  "Hoodies or non-uniform jackets",
                  "Ripped or torn clothing"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-red-600">
                    <span>❌</span> {item}
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Where to Buy</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-[#002147]">YCPS Uniform Shop</p>
                <p className="text-gray-600">Located at the Main Campus, Administration Building</p>
                <p className="text-gray-600">Open: Monday - Friday, 8 AM - 4 PM</p>
                <p className="text-gray-600">Contact: (+055-2184037)</p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const TechnologyPolicy = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Laptop className="w-8 h-8" />
              <CardTitle className="text-2xl">Technology Use Policy</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Purpose</h3>
              <p className="text-gray-600">Technology is an important tool for learning at YCPS. This policy outlines the responsible use of school technology resources and personal devices to ensure a safe and productive learning environment.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">School-Provided Technology</h3>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Computer Lab Rules</h4>
                <ul className="list-disc list-inside text-blue-700 space-y-1">
                  <li>Use computers only for educational purposes</li>
                  <li>Log in with your assigned student credentials only</li>
                  <li>Do not install software or change settings</li>
                  <li>Report any technical issues to the teacher</li>
                  <li>No food or drinks in the computer lab</li>
                  <li>Log out properly when finished</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Mobile Phone Policy</h3>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                <p className="text-yellow-800 font-semibold">Important</p>
                <p className="text-yellow-700 text-sm mt-1">Mobile phones are NOT permitted during school hours for Primary and JHS students. Phones must be:</p>
                <ul className="text-yellow-700 text-sm mt-2 list-disc list-inside">
                  <li>Switched off during school hours</li>
                  <li>Kept in school bags (not pockets)</li>
                  <li>Not used for calls, texts, or photography</li>
                </ul>
              </div>
              <p className="text-gray-600 mt-3">Violation may result in confiscation. Phones will be returned to parents only.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Internet Usage</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800">✓ Acceptable Use</h4>
                  <ul className="text-sm text-green-700 mt-2 space-y-1">
                    <li>• Educational research</li>
                    <li>• School assignments</li>
                    <li>• Teacher-approved activities</li>
                    <li>• Accessing school portal</li>
                  </ul>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-800">✗ Prohibited Use</h4>
                  <ul className="text-sm text-red-700 mt-2 space-y-1">
                    <li>• Social media during school</li>
                    <li>• Gaming websites</li>
                    <li>• Inappropriate content</li>
                    <li>• Cyberbullying</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Digital Citizenship</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Treat others online with respect</li>
                <li>Protect your personal information</li>
                <li>Report cyberbullying to a teacher immediately</li>
                <li>Give credit to sources when using online information</li>
                <li>Think before you post - digital footprints are permanent</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Consequences</h3>
              <div className="space-y-2">
                {[
                  { level: "First Offense", action: "Warning and parent notification" },
                  { level: "Second Offense", action: "Loss of technology privileges (1 week)" },
                  { level: "Third Offense", action: "Parent conference and extended restriction" },
                  { level: "Severe Violation", action: "Suspension and possible expulsion" }
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                    <span className="font-semibold text-[#002147]">{item.level}</span>
                    <span className="text-gray-600 text-sm">{item.action}</span>
                  </div>
                ))}
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const AttendancePolicy = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <UserCheck className="w-8 h-8" />
              <CardTitle className="text-2xl">Attendance Policy</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Importance of Attendance</h3>
              <p className="text-gray-600">Regular attendance is essential for academic success. Students who attend school regularly perform better academically, develop positive social skills, and are better prepared for future success.</p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Attendance Requirements</h3>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-800 font-semibold">Minimum Attendance: 90%</p>
                <p className="text-blue-700 text-sm mt-1">Students must maintain at least 90% attendance to be eligible for promotion to the next grade level.</p>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">School Hours</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Arrival Time</h4>
                  <p className="text-gray-600">7:15 AM - 7:30 AM</p>
                  <p className="text-sm text-gray-500">Classes begin at 7:30 AM sharp</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Late Arrival</h4>
                  <p className="text-gray-600">After 7:35 AM</p>
                  <p className="text-sm text-gray-500">Requires late slip from office</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Excused Absences</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li><strong>Illness:</strong> With parent note or medical certificate</li>
                <li><strong>Medical/Dental Appointments:</strong> With appointment card</li>
                <li><strong>Family Emergency:</strong> Death or serious illness in family</li>
                <li><strong>Religious Observances:</strong> With prior notification</li>
                <li><strong>School-Approved Activities:</strong> Field trips, competitions</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Reporting Absences</h3>
              <div className="space-y-3">
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-[#FF6B00] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <p className="text-gray-600">Call the school office before 8:00 AM on the day of absence</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-[#FF6B00] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <p className="text-gray-600">Provide written excuse note upon student's return</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-[#FF6B00] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <p className="text-gray-600">Submit medical certificate if absent for more than 2 days</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Consequences of Poor Attendance</h3>
              <div className="space-y-2">
                {[
                  { absences: "5 unexcused absences", action: "Parent notification letter" },
                  { absences: "10 unexcused absences", action: "Mandatory parent conference" },
                  { absences: "15 unexcused absences", action: "Academic probation" },
                  { absences: "20+ unexcused absences", action: "Risk of grade retention" }
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                    <span className="text-[#002147]">{item.absences}</span>
                    <span className="text-gray-600 text-sm">{item.action}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Perfect Attendance Recognition</h3>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-green-800">Students with perfect attendance (100%) each term receive:</p>
                <ul className="text-green-700 mt-2 list-disc list-inside">
                  <li>Certificate of Perfect Attendance</li>
                  <li>Recognition at assembly</li>
                  <li>Special privilege rewards</li>
                </ul>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const AntiBullyingPolicy = () => {
  const navigate = useNavigate();
  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2" size={16} /> Back
        </Button>
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8" />
              <CardTitle className="text-2xl">Anti-Bullying Policy</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <p className="text-blue-800 font-semibold">Our Commitment</p>
              <p className="text-blue-700 text-sm">YCPS is committed to providing a safe, caring, and respectful environment where all students can learn and thrive. Bullying of any kind is not tolerated.</p>
            </div>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">What is Bullying?</h3>
              <p className="text-gray-600 mb-3">Bullying is repeated aggressive behavior intended to hurt, harm, or humiliate another person. It can take many forms:</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Physical Bullying</h4>
                  <p className="text-sm text-gray-600">Hitting, kicking, pushing, taking belongings</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Verbal Bullying</h4>
                  <p className="text-sm text-gray-600">Name-calling, insults, threats, teasing</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Social Bullying</h4>
                  <p className="text-sm text-gray-600">Exclusion, spreading rumors, embarrassing someone</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#002147]">Cyberbullying</h4>
                  <p className="text-sm text-gray-600">Online harassment, mean messages, sharing embarrassing content</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Reporting Bullying</h3>
              <p className="text-gray-600 mb-3">If you or someone you know is being bullied, please report it immediately:</p>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Tell a trusted adult - teacher, parent, counselor</li>
                <li>Report to the school office</li>
                <li>Use the anonymous reporting box in the library</li>
                <li>Email: safeschool@ycpschool.com</li>
              </ol>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">How We Respond</h3>
              <div className="space-y-3">
                {[
                  { step: 1, action: "Investigation", desc: "All reports are taken seriously and investigated promptly" },
                  { step: 2, action: "Support", desc: "Victims receive counseling and protection measures" },
                  { step: 3, action: "Intervention", desc: "Bullies receive counseling and appropriate consequences" },
                  { step: 4, action: "Follow-up", desc: "Ongoing monitoring to ensure the bullying has stopped" }
                ].map((item) => (
                  <div key={item.step} className="flex gap-4 items-start">
                    <div className="w-8 h-8 bg-[#FF6B00] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">{item.step}</div>
                    <div>
                      <p className="font-semibold text-[#002147]">{item.action}</p>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Consequences for Bullying</h3>
              <div className="space-y-2">
                {[
                  { level: "First Offense", action: "Warning, parent contact, counseling" },
                  { level: "Second Offense", action: "Detention, mandatory counseling sessions" },
                  { level: "Third Offense", action: "Suspension (1-3 days)" },
                  { level: "Severe/Repeated", action: "Extended suspension or expulsion" }
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                    <span className="font-semibold text-[#002147]">{item.level}</span>
                    <span className="text-gray-600 text-sm">{item.action}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-[#002147] mb-3">Be an Upstander</h3>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-green-800 font-semibold mb-2">How you can help stop bullying:</p>
                <ul className="list-disc list-inside text-green-700 space-y-1">
                  <li>Don't laugh at or encourage bullying behavior</li>
                  <li>Stand up for the person being bullied if it's safe</li>
                  <li>Include everyone - don't leave anyone out</li>
                  <li>Report bullying to an adult</li>
                  <li>Be a good friend and support those who are hurt</li>
                </ul>
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Mail className="mr-2" size={16} /> Report Bullying
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default {
  MedicalReleaseForm,
  FieldTripPermission,
  TranscriptRequest,
  ScheduleChangeForm,
  BusRoutes,
  ParkingInfo,
  CarpoolProgram,
  TrafficGuidelines,
  LunchMenu,
  NutritionInfo,
  MealPlans,
  AllergyGuidelines,
  TutoringServices,
  StudyGuides,
  ResearchDatabases,
  AcademicCalendar,
  NurseOffice,
  ImmunizationRequirements,
  EmergencyProcedures,
  MentalHealthResources,
  StudentHandbook,
  DressCode,
  TechnologyPolicy,
  AttendancePolicy,
  AntiBullyingPolicy
};
